// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions } from '@tanstack/react-query';

import { client } from '../client.gen';
import {
  geocoderApiV1Autocomplete,
  geocoderApiV1Place,
  geocoderApiV1Reverse,
  geocoderApiV1Search,
  geocoderApiV1SearchStructured,
  type Options,
} from '../sdk.gen';
import type {
  V1AutocompleteData,
  V1AutocompleteResponse,
  V1PlaceData,
  V1PlaceResponse,
  V1ReverseData,
  V1ReverseResponse,
  V1SearchData,
  V1SearchResponse,
  V1SearchStructuredData,
  V1SearchStructuredResponse,
} from '../types.gen';

export type QueryKey<TOptions extends Options> = [
  Pick<TOptions, 'baseUrl' | 'body' | 'headers' | 'path' | 'query'> & {
    _id: string;
    _infinite?: boolean;
    tags?: ReadonlyArray<string>;
  },
];

const createQueryKey = <TOptions extends Options>(
  id: string,
  options?: TOptions,
  infinite?: boolean,
  tags?: ReadonlyArray<string>,
): [QueryKey<TOptions>[0]] => {
  const params: QueryKey<TOptions>[0] = {
    _id: id,
    baseUrl: options?.baseUrl || (options?.client ?? client).getConfig().baseUrl,
  } as QueryKey<TOptions>[0];
  if (infinite) {
    params._infinite = infinite;
  }
  if (tags) {
    params.tags = tags;
  }
  if (options?.body) {
    params.body = options.body;
  }
  if (options?.headers) {
    params.headers = options.headers;
  }
  if (options?.path) {
    params.path = options.path;
  }
  if (options?.query) {
    params.query = options.query;
  }
  return [params];
};

export const geocoderApiV1SearchQueryKey = (options: Options<V1SearchData>) => createQueryKey('v1Search', options);

/**
 * Forward geocoding to find a place by searching for an address or name
 *
 * Geocoding is the process of matching an address or other text to its corresponding geographic coordinates.
 */
export const geocoderApiV1SearchOptions = (options: Options<V1SearchData>) =>
  queryOptions<V1SearchResponse, DefaultError, V1SearchResponse, ReturnType<typeof geocoderApiV1SearchQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await geocoderApiV1Search({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      });
      return data;
    },
    queryKey: geocoderApiV1SearchQueryKey(options),
  });

export const geocoderApiV1ReverseQueryKey = (options: Options<V1ReverseData>) => createQueryKey('v1Reverse', options);

/**
 * Reverse geocoding to find what is located at a certain coordinate location
 *
 * Reverse geocoding is used for finding places or addresses near a latitude, longitude pair—like clicking on a map to see what's there when the map doesn't show it otherwise.
 */
export const geocoderApiV1ReverseOptions = (options: Options<V1ReverseData>) =>
  queryOptions<V1ReverseResponse, DefaultError, V1ReverseResponse, ReturnType<typeof geocoderApiV1ReverseQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await geocoderApiV1Reverse({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      });
      return data;
    },
    queryKey: geocoderApiV1ReverseQueryKey(options),
  });

export const geocoderApiV1AutocompleteQueryKey = (options: Options<V1AutocompleteData>) =>
  createQueryKey('v1Autocomplete', options);

/**
 * Autocomplete to give real-time result suggestions without having to type the whole location
 *
 * If you are building an end-user application, you can use the /autocomplete endpoint alongside /search to enable real-time feedback. This type-ahead functionality helps users find what they are looking for, without requiring them to fully specify their search term. Typically, the user starts typing and a drop-down list appears where they can choose the term from the list below.
 */
export const geocoderApiV1AutocompleteOptions = (options: Options<V1AutocompleteData>) =>
  queryOptions<
    V1AutocompleteResponse,
    DefaultError,
    V1AutocompleteResponse,
    ReturnType<typeof geocoderApiV1AutocompleteQueryKey>
  >({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await geocoderApiV1Autocomplete({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      });
      return data;
    },
    queryKey: geocoderApiV1AutocompleteQueryKey(options),
  });

export const geocoderApiV1SearchStructuredQueryKey = (options?: Options<V1SearchStructuredData>) =>
  createQueryKey('v1SearchStructured', options);

/**
 * Structured Geocoding (beta) to find a place with data already separated into housenumber, street, city, etc
 *
 * With structured geocoding, you can search for the individual parts of a location.
 */
export const geocoderApiV1SearchStructuredOptions = (options?: Options<V1SearchStructuredData>) =>
  queryOptions<
    V1SearchStructuredResponse,
    DefaultError,
    V1SearchStructuredResponse,
    ReturnType<typeof geocoderApiV1SearchStructuredQueryKey>
  >({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await geocoderApiV1SearchStructured({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      });
      return data;
    },
    queryKey: geocoderApiV1SearchStructuredQueryKey(options),
  });

export const geocoderApiV1PlaceQueryKey = (options: Options<V1PlaceData>) => createQueryKey('v1Place', options);

/**
 * Place endpoint for details on a place returned from a previous query
 *
 * When you know an identification number and the source it came from, you can use Pelias to get details on the location.
 */
export const geocoderApiV1PlaceOptions = (options: Options<V1PlaceData>) =>
  queryOptions<V1PlaceResponse, DefaultError, V1PlaceResponse, ReturnType<typeof geocoderApiV1PlaceQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
      const { data } = await geocoderApiV1Place({
        ...options,
        ...queryKey[0],
        signal,
        throwOnError: true,
      });
      return data;
    },
    queryKey: geocoderApiV1PlaceQueryKey(options),
  });
